import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import FastAPIInstance from '../../../services/risksystem3-public-api';
import { KidsDataPdfVersion } from './kids/types/KidsData.types';
import { AxiosError, AxiosResponse } from 'axios';
import { toast } from '../../toast/Toast';

export interface KidFiguresSuccessResponse {
  status: 'success';
}
export interface KidFiguresPartialSuccessResponse {
  status: 'partial_success';
  message: string;
  failed_share_classes: string[];
}

export type KidFiguresResponse =
  | KidFiguresSuccessResponse
  | KidFiguresPartialSuccessResponse;

type LegacyApiResponse = {
  Status: 'success';
};

export type MarkKidReviewedPayload = {
  fund_id: string;
  share_class: string;
  document_language: string;
};

export async function markKidReviewed(
  clientId: string,
  payload: MarkKidReviewedPayload,
) {
  const response = await FastAPIInstance.post<LegacyApiResponse>(
    `/v1/raidr/mark_kid_as_reviewed/${clientId}`,
    payload,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data;
}

export function usePublishDocumentMutation() {
  return useMutation({
    mutationFn: (data: FastAPIPublishKIDPayload) => publishDocument(data),
  });
}

export interface FastAPIPublishKIDPayload {
  client_id: string;
  fund_id: string;
  fund_id_string: string;
  share_class: string;
  document_language: string;
  user_id?: string;
  user_name?: string;
}

export async function publishDocument(payload: FastAPIPublishKIDPayload) {
  const response = await FastAPIInstance.post<{ message: string }>(
    'v1/raidr/publish_kid_document',
    payload,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data;
}

export interface RevertKIDPayload {
  client_id: string;
  fund_id: string;
  share_class: string;
  // Keep legacy server spelling to match API contract
  document_langauge: string;
  version: string;
}

export function useRevertKidToPreviousVersionMutation() {
  return useMutation({
    mutationFn: (payload: RevertKIDPayload) =>
      revertKidToPreviousVersion(payload),
    onError: (error: AxiosError<{ detail?: string | Array<{ msg?: string }>; message?: string }>) => {
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : error.response?.data?.message ?? error.message ?? 'An error occurred while reverting.';
      toast({
        title: 'Revert failed',
        message,
        type: 'error',
      });
    },
  });
}

export async function revertKidToPreviousVersion(payload: RevertKIDPayload) {
  const response = await FastAPIInstance.post<{ message: string }>(
    'v1/raidr/revert_kid_to_previous_version',
    payload,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data;
}

type TemplateUpdate = {
  template_id: string;
  text: string;
};

export interface WriteKidDataPayload {
  user_name?: string;
  kid_data: KidsDataPdfVersion[];
  template_updates: TemplateUpdate[];
  increment_version_number?: boolean;
}

export function useUpdateKidData(
  options?: UseMutationOptions<
    AxiosResponse<any>, // TData
    AxiosError<any>, // TError
    WriteKidDataPayload // TVariables - POST DATA
  >,
) {
  return useMutation<AxiosResponse<any>, AxiosError<any>, WriteKidDataPayload>({
    mutationFn: (data: WriteKidDataPayload) => updateKidData(data),
    onError: (error) => {
      toast({
        title: 'Changes not saved',
        message: 'There was an error saving the changes. Please try again.',
        type: 'error',
      });
      console.error('Figure update failed:', error);
    },
    ...options,
  });
}

export async function updateKidData(payload: WriteKidDataPayload) {
  const response = await FastAPIInstance.post(
    'v1/raidr/write_kid_data',
    payload,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response.data;
}

type UpdateFiguresPayload = {
  fund_id: string;
  share_classes?: string[];
};

export function useWriteCurrentKidFiguresToDocument(
  clientId: string,
  options?: UseMutationOptions<
    AxiosResponse<any>, // TData
    AxiosError<any>, // TError
    UpdateFiguresPayload // TVariables
  >,
) {
  return useMutation({
    mutationFn: (payload) =>
      writeCurrentKidFiguresToDocument(clientId, payload),

    onError: (error) => {
      toast({
        title: 'Failed to update figures.',
        message: `${error.message}`,
        type: 'error',
      });
      console.error('Figure update failed:', error);
    },
    ...options,
  });
}

export async function writeCurrentKidFiguresToDocument(
  clientId: string,
  payload: UpdateFiguresPayload,
): Promise<AxiosResponse> {
  return FastAPIInstance.post(
    `v1/raidr/write_current_figures_to_doc/${clientId}`,
    payload,
    {
      headers: { 'Content-Type': 'application/json' },
    },
  );
}
